/**
 * Cookie Settings Modal Component
 * Manages the advanced cookie settings modal with toggles for different categories
 */

import { getCookieManager } from '../utils/cookieManager.js';

export class CookieSettings {
  constructor() {
    this.cookieManager = getCookieManager();
    this.modal = null;
    this.overlay = null;
    this.closeBtn = null;
    this.saveBtn = null;
    this.toggles = {};
    this.init();
  }

  /**
   * Initialize the cookie settings modal
   */
  init() {
    this.modal = document.getElementById('cookieSettingsModal');
    this.overlay = document.getElementById('cookieSettingsOverlay');

    if (!this.modal || !this.overlay) {
      console.warn('Cookie settings modal elements not found');
      return;
    }

    this.closeBtn = this.modal.querySelector('[data-cookie-close]');
    this.saveBtn = this.modal.querySelector('[data-cookie-save]');

    // Get all toggle inputs
    this.toggles = {
      essential: this.modal.querySelector('[data-cookie-toggle="essential"]'),
      marketing: this.modal.querySelector('[data-cookie-toggle="marketing"]'),
      functional: this.modal.querySelector('[data-cookie-toggle="functional"]'),
      analytics: this.modal.querySelector('[data-cookie-toggle="analytics"]')
    };

    this.loadCurrentPreferences();
    this.attachEventListeners();
  }

  /**
   * Load current preferences into toggles
   */
  loadCurrentPreferences() {
    const preferences = this.cookieManager.getPreferences();

    Object.keys(this.toggles).forEach(category => {
      const toggle = this.toggles[category];
      if (toggle) {
        toggle.checked = preferences[category];

        // Essential cookies toggle is always disabled
        if (category === 'essential') {
          toggle.disabled = true;
          toggle.checked = true;
        }
      }
    });
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Save button
    if (this.saveBtn) {
      this.saveBtn.addEventListener('click', () => this.handleSave());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Listen for open settings event from banner
    window.addEventListener('openCookieSettings', () => this.open());

    // Listen for clicks on footer cookie settings link
    this.handleFooterLinkClick = (e) => {
      const target = e.target;
      if (target && target.hasAttribute('data-cookie-settings')) {
        e.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleFooterLinkClick);

    // ESC key to close
    this.handleKeyDown = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Open the modal
   */
  open() {
    if (this.modal && this.overlay) {
      this.loadCurrentPreferences();
      this.modal.classList.add('visible');
      this.overlay.classList.add('visible');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Close the modal
   */
  close() {
    if (this.modal && this.overlay) {
      this.modal.classList.remove('visible');
      this.overlay.classList.remove('visible');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  /**
   * Check if modal is open
   */
  isOpen() {
    return this.modal && this.modal.classList.contains('visible');
  }

  /**
   * Handle save button click
   */
  handleSave() {
    const preferences = {
      essential: true, // Always true
      marketing: this.toggles.marketing?.checked || false,
      functional: this.toggles.functional?.checked || false,
      analytics: this.toggles.analytics?.checked || false
    };

    this.cookieManager.savePreferences(preferences);
    this.close();

    // Hide the banner if it's visible
    const banner = document.getElementById('cookieBanner');
    if (banner) {
      banner.classList.remove('visible');
      banner.setAttribute('aria-hidden', 'true');
    }

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: preferences
    }));
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.close);
    }
    if (this.saveBtn) {
      this.saveBtn.removeEventListener('click', this.handleSave);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.close);
    }
    window.removeEventListener('openCookieSettings', this.open);
    document.removeEventListener('click', this.handleFooterLinkClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}

/**
 * Initialize cookie settings modal
 * @returns {CookieSettings} Instance of cookie settings modal
 */
export function initCookieSettings() {
  return new CookieSettings();
}
